import NumberFlow from '@number-flow/react';
import { BadgeCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card } from 'src/components/ui/card';
import { ForecastPaginatedProductDetailsLine } from 'src/features/forecastPaginated/forecastPaginatedDetails/ForecastPaginatedProductDetailsLine';
import { ProductBadges } from 'src/features/forecastPaginated/forecastPaginatedDetails/ProductBadges';
import { cn } from 'src/lib/utils';
import { useSkuDetailQuery } from 'src/services/api/getSkuDetail';

const NUMERIC_SKU_PATTERN = /^(0|[1-9]\d*)$/;

type ForecastPaginatedProductDetailsProps = {
  sku: string;
  fullWidth: boolean;
  validatedAt?: string | null;
};

export function ForecastPaginatedProductDetails({
  sku,
  fullWidth,
  validatedAt,
}: ForecastPaginatedProductDetailsProps) {
  const { data } = useSkuDetailQuery(sku);
  const { t } = useTranslation('forecast');
  const numericSku = NUMERIC_SKU_PATTERN.test(sku) ? Number(sku) : null;

  return (
    <Card
      className={cn(
        'mr-0 flex h-44 w-full flex-col gap-y-4 overflow-hidden p-4 lg:mr-4',
        fullWidth && 'lg:mr-0 lg:w-full',
      )}
    >
      <div className='flex flex-col gap-y-1'>
        <div className='flex flex-row items-center'>
          {validatedAt && (
            <BadgeCheck className='mr-2 size-5 text-success-default' />
          )}
          {numericSku === null ? (
            <span className='font-semibold text-lg'>{sku}</span>
          ) : (
            <NumberFlow
              className='font-semibold text-lg'
              format={{ useGrouping: false }}
              value={numericSku}
            />
          )}
          {data.internal_title && (
            <span className='ml-2 truncate text-lg'>
              - {data.internal_title}
            </span>
          )}
        </div>
        <ProductBadges
          isDestocking={data.is_destocking}
          isNovelty={data.is_novelty}
        />
      </div>
      <div
        className={cn(
          'flex flex-col text-xs',
          data.is_novelty || data.is_destocking ? 'gap-y-2' : 'gap-y-4',
        )}
      >
        <div className='flex flex-row justify-between gap-x-4'>
          <ForecastPaginatedProductDetailsLine
            fullWidth={fullWidth}
            label={t('First stock entry')}
            value={data.product_first_stock_entry_at_date_key}
          />
          <ForecastPaginatedProductDetailsLine
            fullWidth={fullWidth}
            label={t('Life segment')}
            value={t(data.life_segment_label)}
          />
        </div>
        <div className='flex w-full flex-row justify-between gap-x-4'>
          <ForecastPaginatedProductDetailsLine
            fullWidth={fullWidth}
            label={t('Last reviewed date')}
            value={data.last_reviewed_date}
          />
          <ForecastPaginatedProductDetailsLine
            fullWidth={fullWidth}
            label={t('Rotation segment sales')}
            tooltip={t(data.rotation_segment_sales.label)}
            value={`${data.rotation_segment_sales.value}`}
          />
        </div>
        <div className='flex w-full flex-row justify-between gap-x-4'>
          <ForecastPaginatedProductDetailsLine
            fullWidth={fullWidth}
            label={t('Launch date')}
            value={data.product_started_at_date_key}
          />
          <ForecastPaginatedProductDetailsLine
            fullWidth={fullWidth}
            label={t('Rotation segment forecast')}
            tooltip={t(data.rotation_segment_forecast.label)}
            value={`${data.rotation_segment_forecast.value}`}
          />
          {/* <ForecastPaginatedProductDetailsLine
            label={t('Criteria')}
            value='Le nombre de ventre possède un écrat supérieur à 10 par rapport aux
              données du forecast sur le mois précédent (en dur par encore la data)'
            fullWidth={fullWidth}
          /> */}
        </div>
      </div>
    </Card>
  );
}
